/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import GameSetup, { GameType } from '../../../Game/GameSetup';
import StateDependencyComponent from '../../../StateManager/StateDependencyComponent';
import StateEvent from '../../../StateManager/StateEvent';
import { StateID } from '../../../StateManager/StateID';
import StateManager from '../../../StateManager/StateManager';
import StateReference from '../../../StateManager/StateReference';
import InjectableComponent from '../ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../ComponentReference/ReferenceGroup';
import ObservableDoubleComponent from '../Observable/ObservableDoubleComponent';
import ObservableIntComponent from '../Observable/ObservableIntComponent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

export enum BetComponent {
    Denom,
    Multiplier,
    Lines
}

/**
 * Shows or hides the associated betting option based on the game's configuration.
 */
@cc._decorator.ccclass
export default class OptionalBetComponent extends StateDependencyComponent
{
    /** The state on which to check if the component should be shown. */
    @property({ type: StateReference, visible: true })
    private _checkOnState: StateReference = new StateReference();

    /** The type of bet component this script is attached to. */
    @property({ type: cc.Enum(BetComponent), visible: true })
    private _betComponent: BetComponent = BetComponent.Denom;

    @property({ type: [cc.Node], visible: true })
    private _inputComponents: cc.Node[] = [];

    /** @hidden */
    private _denomObservable: InjectableComponent<ObservableDoubleComponent>
        = new InjectableComponent<ObservableDoubleComponent>(ReferenceGroup.MainGame, "BetManager.BetDenom");
    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent<GameSetup>();
    /** @hidden */
    private _linesObservable: InjectableComponent<ObservableIntComponent>
        = new InjectableComponent<ObservableIntComponent>(ReferenceGroup.MainGame, "BetManager.Lines");
    /** @hidden */
    private _multiplierObservable: InjectableComponent<ObservableIntComponent>
        = new InjectableComponent<ObservableIntComponent>(ReferenceGroup.MainGame, "BetManager.BetMultiplier");
    /** @hidden */
    private _stateManagerRef: InjectableComponent<StateManager> = new InjectableComponent<StateManager>();

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this._denomObservable.SetContext(ObservableDoubleComponent);
        this._gameSetupRef.SetContext(GameSetup);
        this._linesObservable.SetContext(ObservableIntComponent);
        this._multiplierObservable.SetContext(ObservableIntComponent);
        this._stateManagerRef.SetContext(StateManager);
    }

    /** @hidden */
    protected start(): void
    {
        super.start([this._checkOnState]);
    }

    /** @hidden */
    public onStateChangeHandler = (event: StateEvent): void =>
    {
        if (event.State === this._checkOnState.Value)
        {
            this.checkDisplay();
            this.completeState(this._checkOnState.Value);
        }
    }

    /** @hidden */
    protected onEnable(): void
    {
        this._stateManagerRef.WithValue(stateManager =>
        {
            if (!stateManager.CurrentState) { return; }
            const stateId: StateID = stateManager.CurrentState.StateID;
            if (stateId == null || stateId === StateID.Loading || stateId === StateID.Initializing) { return; }
            this.checkDisplay();
        });
    }

    /** @hidden */
    private checkDisplay(): void
    {
        switch (this._betComponent)
        {
            case BetComponent.Denom:
                if (this.checkDenom()) { this.checkInputs(this._denomObservable.Value); }
                else { this.node.active = false; }
                break;
            case BetComponent.Multiplier:
                if (this.checkBetMultiplier()) { this.checkInputs(this._multiplierObservable.Value); }
                else { this.node.active = false; }
                break;
            case BetComponent.Lines:
                this.node.active = this.checkLines();
                break;
        }
    }

     /** hidden */
     private checkDenom(): boolean
     {
         return !this.isSocial;
     }
 
     /** @hidden */
     private checkBetMultiplier(): boolean
     {
         return (!this.isSocial && this.isSelectable(this._multiplierObservable.Value)) || this.isSocial;
     }
 
     /** @hidden */
     private checkLines(): boolean
     {
         return !this.isSocial && this.isSelectable(this._linesObservable.Value);
     }
 
     /** hidden */
     private checkInputs(observable: ObservableDoubleComponent): void
     {
         if (!this.isSelectable(observable))
         {
             for (let input of this._inputComponents)
             {
                 input.active = false;
             }
         }
     }
 
     private isSelectable(observable: ObservableDoubleComponent): boolean
     {
         return !observable.IsValueRestricted || observable.MinValue !== observable.MaxValue;
     }
 
     private get isSocial(): boolean { return this._gameSetupRef.WithValue(g => g.GameStyle === GameType.Social, false); }
}
